import { BigNumber } from '@0x/utils'
import assetUtils, { Asset } from 'app-lib/assetUtils'
import iTokensUtils from 'app-lib/iTokensUtils'
import { AnyProvider } from 'app-lib/providerUtils'
import IBorrowedFundsState, { LoanReturnData } from 'bzx-common/src/domain/IBorrowedFundsState'
import appConfig, { chainConfigs } from '../config/appConfig'
import IDepositEstimate from '../domain/borrow/IDepositEstimate'
import ICollateralChangeEstimate from '../domain/ICollateralChangeEstimate'
import ILoanParams from '../domain/ILoanParams'
// import bzxApi from './apis/bzxApi'
import oracleApi from './apis/oracleApi'
import { ChainId } from '../config/chains/chainCommon'
import providerUtils from 'app-lib/providerUtils'

const getPositionSafetyText = (
  maintenanceMargin: BigNumber,
  collateralizedPercent: BigNumber
): string => {
  const liquidationZone = maintenanceMargin.div(10 ** 20).toNumber()
  const dangerZone = liquidationZone + 0.1
  if (collateralizedPercent.gt(dangerZone)) {
    return 'Safe'
  } else if (collateralizedPercent.gt(liquidationZone)) {
    return 'Danger'
  } else if (collateralizedPercent.eq(0)) {
    return 'Display Error'
  } else {
    return 'Liquidation Pending'
  }
}

/** APR, interest the owner of the loan or a margin position */
const getBorrowInterestRate = async (provider: AnyProvider, asset: Asset): Promise<BigNumber> => {
  const { contractsSource } = providerUtils.getDependenciesStrictRead(provider)
  const assetContract = contractsSource.getITokenContract(asset)
  const result = await assetContract.borrowInterestRate().callAsync()
  return result.div(10 ** 18)
}

async function getLoanParamsOnChain(
  provider: AnyProvider,
  loanAsset: Asset,
  collateralAsset: Asset,
  isBorrow: boolean
): Promise<ILoanParams> {
  const { contractsSource, chainId } = providerUtils.getDependenciesStrictRead(provider)
  const bzxContract = contractsSource.getiBZxContract()
  const collateralTokenAddress = assetUtils.getAddress(chainId, collateralAsset)
  const loanTokenAddress = assetUtils.getAddress(chainId, loanAsset)
  const [loanParams] = await bzxContract
    .getDefaultLoanParams(loanTokenAddress, collateralTokenAddress, isBorrow)
    .callAsync()
  return { loanId: loanParams.id, ...loanParams }
}

const getLoanParams = async (
  provider: AnyProvider,
  asset: Asset,
  collateralAsset: Asset,
  isBorrow: boolean
): Promise<ILoanParams> => {
  // Keeping this code until the backend api works again, or, we decide to get params only onchain.

  // try {
  //   const platform = isBorrow ? 'Torque' : 'Fulcrum'
  //   const loanParamFromApi = await bzxApi.getLoanParams(
  //     provider.currentChainId,
  //     platform,
  //     asset,
  //     collateralAsset
  //   )
  //   if (!loanParamFromApi) {
  //     throw new Error('Loan params not found in API')
  //   }
  //   return {
  //     loanId: loanParamFromApi.loanId,
  //     loanToken: loanParamFromApi.principal,
  //     collateralToken: loanParamFromApi.collateral,
  //     minInitialMargin: new BigNumber(loanParamFromApi.initialMargin).times(10 ** 18),
  //     maintenanceMargin: new BigNumber(loanParamFromApi.maintenanceMargin).times(10 ** 18),
  //   } as ILoanParams
  // } catch (err) {
  //   console.error(
  //     `Failing API getLoanParams | asset: ${asset} | collateralAsset: ${collateralAsset}; trying from blockchain...`,
  //     err
  //   )
  // }

  return getLoanParamsOnChain(provider, asset, collateralAsset, isBorrow)
}

const formatLoan = (
  data: LoanReturnData,
  chainId: ChainId,
  account: string
): IBorrowedFundsState => {
  let loanAsset = assetUtils.getAssetFromAddress(chainId, data.loanToken)
  // could be WETH address instead of ETH, so replace it with native token for sure
  loanAsset = assetUtils.isNativeTokenForNetwork(chainId, loanAsset)
    ? chainConfigs[chainId].native.asset
    : loanAsset
  let collateralAsset = assetUtils.getAssetFromAddress(chainId, data.collateralToken)
  collateralAsset = assetUtils.isNativeTokenForNetwork(chainId, collateralAsset)
    ? chainConfigs[chainId].native.asset
    : collateralAsset

  const loanPrecision = assetUtils.getDecimals(chainId, loanAsset)
  const collateralPrecision = assetUtils.getDecimals(chainId, collateralAsset)
  let amountOwned = data.principal.minus(data.interestDepositRemaining)
  amountOwned = amountOwned.lte(0)
    ? new BigNumber(0)
    : amountOwned.dividedBy(10 ** loanPrecision).dp(3, BigNumber.ROUND_CEIL)

  const interestRate = data.interestOwedPerDay.dividedBy(data.principal).multipliedBy(365)

  return {
    accountAddress: account,
    loanId: data.loanId,
    loanAsset: loanAsset,
    collateralAsset: collateralAsset,
    amount: data.principal.dividedBy(10 ** loanPrecision).dp(3, BigNumber.ROUND_CEIL),
    amountOwed: amountOwned,
    collateralAmount: data.collateral.dividedBy(10 ** collateralPrecision),
    collateralizedPercent: data.currentMargin.dividedBy(10 ** 20),
    interestRate,
    interestOwedPerDay: data.interestOwedPerDay.dividedBy(10 ** loanPrecision),
    hasManagementContract: true,
    isInProgress: false,
    loanData: data,
  }
}

const getLoanById = async (
  provider: AnyProvider,
  loanId: string
): Promise<IBorrowedFundsState | null> => {
  const { contractsSource, chainId, userAddress } = providerUtils.getDependenciesStrict(provider)
  const bzxContract = contractsSource.getiBZxContract()
  const loanData = await bzxContract.getLoan(loanId).callAsync()
  const zero = new BigNumber(0)

  if (!loanData.principal.eq(zero) && !loanData.currentMargin.eq(zero)) {
    // What's that test for?
    return formatLoan(loanData, chainId, userAddress)
  }
  return null
}

const getLoansList = async (provider: AnyProvider): Promise<IBorrowedFundsState[]> => {
  const { contractsSource, chainId, userAddress } = providerUtils.getDependenciesStrict(provider)
  const bzxContract = contractsSource.getiBZxContract()

  const loansData = await bzxContract
    .getUserLoans(
      userAddress,
      new BigNumber(0),
      new BigNumber(50),
      2, // Torque loans
      false,
      false
    )
    .callAsync()

  return loansData.map((e: LoanReturnData) => formatLoan(e, chainId, userAddress))
}

function getLoanExtendEstimate(interestOwedPerDay: BigNumber, daysToAdd: number) {
  return interestOwedPerDay.multipliedBy(daysToAdd)
}

async function getDepositAmountEstimate(
  provider: AnyProvider,
  borrowAsset: Asset,
  collateralAsset: Asset,
  amount: BigNumber
): Promise<IDepositEstimate> {
  const { chainId, helperContract } = providerUtils.getDependenciesStrictRead(provider)
  const result = { depositAmount: new BigNumber(0), gasEstimate: new BigNumber(0) }

  const [loanToken, collateralToken] = assetUtils.getTokensInfo(chainId, [
    borrowAsset,
    collateralAsset,
  ])

  if (amount.gt(0)) {
    const borrowEstimate = await helperContract
      .getDepositAmountForBorrow(
        amount.multipliedBy(10 ** loanToken.decimals),
        loanToken.address,
        collateralToken.address
      )
      .callAsync()

    result.depositAmount = borrowEstimate.dividedBy(10 ** collateralToken.decimals)
  }

  return result
}

const getCollateralExcessAmount = async (
  provider: AnyProvider,
  borrowedFundsState: IBorrowedFundsState
): Promise<BigNumber> => {
  let result = new BigNumber(0)
  const { currentChainId } = provider

  if (provider.web3Wrapper && provider.contractsSource && provider.contractsSource.canWrite) {
    const account = provider.currentAccount
    const bzxContract = provider.contractsSource.getiBZxContract()
    if (account && bzxContract) {
      result = await bzxContract
        .withdrawCollateral(borrowedFundsState.loanId, account, appConfig.MAX_UINT)
        .callAsync({
          from: account,
          gas: appConfig.gas.gasLimitFallback,
        })
      const precision = assetUtils.getDecimals(currentChainId, borrowedFundsState.collateralAsset)
      result = result.dividedBy(10 ** precision)
    }
  }
  return result
}

const getLoanCollateralChangeEstimate = async (
  provider: AnyProvider,
  borrowedFundsState: IBorrowedFundsState,
  collateralAmount: BigNumber,
  isWithdrawal: boolean
): Promise<ICollateralChangeEstimate> => {
  const result = {
    collateralAmount: collateralAmount,
    collateralizedPercent: new BigNumber(0),
    liquidationPrice: new BigNumber(0),
    gasEstimate: new BigNumber(0),
    isWithdrawal: isWithdrawal,
  }

  if (provider.contractsSource && provider.web3Wrapper && borrowedFundsState.loanData) {
    const oracleContract = provider.contractsSource.getOracleContract()
    const collateralAsset = assetUtils.getAssetFromAddress(
      provider.currentChainId,
      borrowedFundsState.loanData.collateralToken
    )
    const collateralPrecision = assetUtils.getDecimals(provider.currentChainId, collateralAsset)
    let newAmount = new BigNumber(0)
    if (collateralAmount && collateralAmount.gt(0)) {
      newAmount = collateralAmount.multipliedBy(10 ** collateralPrecision)
    }
    result.collateralAmount = isWithdrawal
      ? new BigNumber(borrowedFundsState.loanData.collateral.minus(newAmount).toFixed(0, 1))
      : new BigNumber(borrowedFundsState.loanData.collateral.plus(newAmount).toFixed(0, 1))
    try {
      const newCurrentMargin: [BigNumber, BigNumber] = await oracleContract
        .getCurrentMargin(
          borrowedFundsState.loanData.loanToken,
          borrowedFundsState.loanData.collateralToken,
          borrowedFundsState.loanData.principal,
          result.collateralAmount
        )
        .callAsync()
      result.collateralizedPercent = newCurrentMargin[0].dividedBy(10 ** 18).plus(100)
    } catch (e) {
      result.collateralizedPercent = borrowedFundsState.collateralizedPercent.times(100).plus(100)
    }
  }

  return result
}

const getBorrowAmountEstimate = async (
  provider: AnyProvider,
  borrowAsset: Asset,
  collateralAsset: Asset,
  depositAmount: BigNumber,
  collaterizationPercent: BigNumber,
  minInitialMargin?: BigNumber
) => {
  const { chainId, helperContract } = providerUtils.getDependenciesStrictRead(provider)

  const result = {
    borrowAsset,
    collateralAsset,
    borrowAmount: new BigNumber(0),
    liquidityAvailable: new BigNumber(0),
    exceedsLiquidity: false,
    collateralToLoanRate: new BigNumber(0),
  }

  if (depositAmount.lte(0)) {
    return result
  }

  const collateralToLoanRate = await oracleApi.getSwapRate(provider, collateralAsset, borrowAsset)
  const liquidity = await iTokensUtils.getAvailableLiquidity(provider, borrowAsset)
  const [loanToken, collateralToken] = assetUtils.getTokensInfo(chainId, [
    borrowAsset,
    collateralAsset,
  ])
  result.liquidityAvailable = liquidity
  result.collateralToLoanRate = collateralToLoanRate

  if (depositAmount.times(collateralToLoanRate).gte(liquidity)) {
    result.borrowAmount = liquidity.times(0.9)
    result.exceedsLiquidity = true
    return result
  }

  const borrowEstimate = await helperContract
    .getBorrowAmountForDeposit(
      depositAmount.times(10 ** collateralToken.decimals),
      loanToken.address,
      collateralToken.address
    )
    .callAsync()

  if (!minInitialMargin) {
    minInitialMargin = (
      await getLoanParams(provider, borrowAsset, collateralAsset, true)
    ).minInitialMargin
      .div(10 ** 18)
      .plus(100)
  }
  // getBorrowAmountForDeposit estimates borrow amount for 20% less collaterization than getDepositAmountForBorrow
  // example: borrow 52USDC for 1ETH (1eth = 100USDC) with desired collaterization 200%
  // by default, it will create a loan with borrowed 52USDC, 1ETH collateral and 180% collaterization
  // so we need to compensate provider 20%
  const realBorrowAmount = borrowEstimate
    .div(collaterizationPercent.plus(20))
    .times(minInitialMargin)
  result.borrowAmount = realBorrowAmount
    .dividedBy(10 ** loanToken.decimals)
    .precision(loanToken.decimals, 1)

  return result
}

const getLiquidationPrice = (
  provider: AnyProvider,
  loan: IBorrowedFundsState,
  collateralAmount: BigNumber,
  activeToken: string
) => {
  const { loanAsset, collateralAsset, loanData } = loan
  const loanAssetDecimals = assetUtils.getDecimals(provider.currentChainId, loanAsset)
  const collateralAssetDecimals = assetUtils.getDecimals(provider.currentChainId, collateralAsset)

  const loanAssetPrecision = new BigNumber(10 ** (18 - loanAssetDecimals))
  const collateralAssetPrecision = new BigNumber(10 ** (18 - collateralAssetDecimals))

  const liquidationCollateralToLoanRate = loanData.maintenanceMargin
    .times(loanData.principal.times(loanAssetPrecision))
    .div(10 ** 20)
    .plus(loanData.principal.times(loanAssetPrecision))
    .div(collateralAmount.times(collateralAssetPrecision))
    .times(10 ** 18)

  const liquidationPrice =
    activeToken === collateralAsset
      ? liquidationCollateralToLoanRate.div(10 ** 18)
      : new BigNumber(1).div(liquidationCollateralToLoanRate.div(10 ** 18))

  return liquidationPrice
}

/**
 * Get expected liquidation price and collateralization percent.
 * @param {IBorrowedFundsState} loan current loan
 * @param {BigNumber} changeAmount how much the amount need to change *
 * @param {bolean} isWithdrawal true if need to decrease collateralization percent false - increase
 * @param {string} activeToken in which coins the liquidation price need to be displayed
 */
const getEstimateDataChangeEstimate = async (
  provider: AnyProvider,
  loan: IBorrowedFundsState,
  changeAmount: BigNumber,
  isWithdrawal: boolean,
  activeToken: string
) => {
  const result = {
    collateralizedPercent: new BigNumber(0),
    liquidationPrice: new BigNumber(0),
  }

  const collateral = await getLoanCollateralChangeEstimate(
    provider,
    loan,
    new BigNumber(changeAmount),
    isWithdrawal
  )
  result.collateralizedPercent = collateral.collateralizedPercent
  result.liquidationPrice = getLiquidationPrice(
    provider,
    loan,
    collateral.collateralAmount,
    activeToken
  )

  return result
}

/**
 * Get expected liquidation price and collateralization percent for Borrow more.
 * @param {BigNumber} changeAmount how much the borrow need to increase
 * @param {string} activeToken in which coins the liquidation price need to be displayed
 */
const getBorrowMoreChangeEstimate = async (
  provider: AnyProvider,
  loan: IBorrowedFundsState,
  changeAmount: BigNumber,
  activeToken: string
) => {
  const borrowMoreLoan = { ...loan }
  borrowMoreLoan.loanData = { ...loan.loanData } // deep copy
  const decimals = assetUtils.getDecimals(provider.currentChainId, loan.loanAsset)
  borrowMoreLoan.loanData.principal = borrowMoreLoan.loanData.principal.plus(
    changeAmount.times(10 ** decimals)
  )

  return await getEstimateDataChangeEstimate(
    provider,
    borrowMoreLoan,
    new BigNumber(0),
    false,
    activeToken
  )
}

/**
 * Get expected liquidation price and collateralization percent for Repay.
 * @param {BigNumber} changeAmount how much the borrow need to decrease
 * @param {string} activeToken in which coins the liquidation price need to be displayed
 */
const getRepayChangeEstimate = async (
  provider: AnyProvider,
  loan: IBorrowedFundsState,
  changeAmount: BigNumber,
  activeToken: string
) => {
  const borrowLessloan = { ...loan }
  borrowLessloan.loanData = { ...loan.loanData } // deep copy
  const decimals = assetUtils.getDecimals(provider.currentChainId, loan.loanAsset)
  borrowLessloan.loanData.principal = borrowLessloan.loanData.principal.minus(
    changeAmount.times(10 ** decimals)
  )

  return await getEstimateDataChangeEstimate(
    provider,
    borrowLessloan,
    new BigNumber(0),
    true,
    activeToken
  )
}

/**
 * Get the interest rate change if user adds or removes liquidity.
 * @param amount (in decimals)
 */
async function nextBorrowInterestRate(provider: AnyProvider, asset: Asset, amount: BigNumber) {
  const { chainId, contractsSource } = providerUtils.getDependenciesStrictRead(provider)
  const decimals = assetUtils.getDecimals(chainId, asset)
  const iToken = contractsSource.getITokenContract(asset)
  return (await iToken.nextBorrowInterestRate(amount.times(10 ** decimals)).callAsync()).div(
    10 ** 18
  )
}

export default {
  getBorrowAmountEstimate,
  getBorrowInterestRate,
  getBorrowMoreChangeEstimate,
  getCollateralExcessAmount,
  getDepositAmountEstimate,
  getEstimateDataChangeEstimate,
  getLiquidationPrice,
  getLoanById,
  getLoanCollateralChangeEstimate,
  getLoanExtendEstimate,
  getLoanParams,
  getLoansList,
  getPositionSafetyText,
  getRepayChangeEstimate,
  nextBorrowInterestRate,
}
